import React, { useState, useEffect } from 'react'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import Headline from '@ds/elements/text/Headline'
import Body from '@ds/elements/text/Body'
import TextInput from '@ds/elements/inputs/TextInput'
import Button from '@ds/elements/inputs/Button'
import { LinkButton } from '@ds/elements/navigation/Link'
import Link from '@ds/elements/navigation/Link'
import GoogleLogomark from '@ds/icons/brands/google-logomark.svg'
import GithubLinkButton from '../onboarding/shared/GithubButton'
import OrSeparator from '../onboarding/shared/OrSeparator'
import LegalFooter from '../onboarding/shared/LegalFooter'
import WelcomeBack from './WelcomeBack'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { webAPI } from '../services/api'
import { idpErrorWorkerReady } from '../auth/idpErrorWorker'
import { idpRejectionMessage } from '../auth/idpRejectionMessage'
import * as styles from './Login.module.css'

function sanitizeReturnUrl(raw) {
  if (!raw) return null
  try {
    const url = new URL(raw, window.location.origin)
    if (url.origin !== window.location.origin) return null
    return url.pathname + url.search + url.hash
  } catch {
    return null
  }
}

const Spinner = () => (
  <svg className={styles.spinner} width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle opacity="0.25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path opacity="0.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
)

function useQueryParams() {
  const searchParams = new URLSearchParams(window.location.search)
  const returnUrl = sanitizeReturnUrl(searchParams.get('return_url'))
  const errorParam = searchParams.get('error')
  const returnUrlQuery = returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : ''
  return { returnUrl, errorParam, returnUrlQuery }
}

const Login = () => {
  useDocumentTitle('Sign in')
  const { returnUrl, errorParam, returnUrlQuery } = useQueryParams()
  const [step, setStep] = useState('checking_auth')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [ssoRequestSubmitted, setSSORequestSubmitted] = useState(false)

  useEffect(() => {
    if (window.location.pathname === '/login/magic-link-sent') {
      setStep('check_email')
      return
    }

    const checkAuth = async () => {
      if (errorParam) {
        setStep('email')
        return
      }

      try {
        await webAPI.getCurrentUser()
        window.location.href = returnUrl || '/'
      } catch (err) {
        setStep('email')
      }
    }

    checkAuth()
  }, [returnUrl, errorParam])

  useEffect(() => {
    if (!errorParam) return

    const errorMessages = {
      'session_expired': 'Your session has expired. Please sign in again.',
      'email_not_verified': 'Your email address has not been verified. Please verify your email first.',
      'account_conflict': 'This account is already linked to another user.',
      'oauth_failed': 'Authentication failed. Please try again.',
      'internal_error': 'An unexpected error occurred. Please try again.',
      'invalid_token': 'This link is invalid or has expired. Please request a new one.',
    }

    if (errorParam === 'idp_rejected') {
      setError(idpRejectionMessage(window.location.search))
    } else if (errorParam.includes(':')) {
      const colonIndex = errorParam.indexOf(':')
      const errorCode = errorParam.substring(0, colonIndex).trim()
      const errorMsg = errorParam.substring(colonIndex + 1).trim()

      if (errorCode === 'SSO_ACCESS_REQUEST_SUBMITTED' || errorCode === 'SSO_ACCESS_REQUEST_PENDING') {
        setSSORequestSubmitted(true)
        setError(null)
      } else {
        setError(errorMsg)
      }
    } else {
      setError(errorMessages[errorParam] || errorParam)
    }
  }, [errorParam])

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const response = await webAPI.initiateEmailLogin(email, returnUrl)
      const { action, sso_authorization_url } = response.data

      if (action === 'sso' && sso_authorization_url) {
        setStep('sso_redirect')
        await idpErrorWorkerReady()
        window.location.href = sso_authorization_url
        return
      }

      if (action === 'password') {
        setStep('password')
        setLoading(false)
        return
      }

      setStep('check_email')
      setLoading(false)
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const response = await webAPI.loginWithPassword(email, password, returnUrl)
      const redirectUrl = response.data?.redirect_url || '/'
      await idpErrorWorkerReady()
      window.location.href = redirectUrl
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.')
      setLoading(false)
    }
  }

  const handleBackToEmail = () => {
    setStep('email')
    setPassword('')
    setError(null)
  }

  const renderEmailStep = () => (
    <form className={styles.form} onSubmit={handleEmailSubmit}>
      <div className={styles.heading}>
        <Headline scale="large">Sign in to Oplane</Headline>
      </div>
      <div className={styles.subheading}>
        <Body scale="large" color="on-surface-variant">
          Welcome back
        </Body>
      </div>

      {error && (
        <div className={styles.errorBanner} role="alert">
          <Body scale="medium">{error}</Body>
        </div>
      )}

      {ssoRequestSubmitted && (
        <div className={styles.successBanner}>
          <Body scale="medium">
            <strong>Access request submitted!</strong> An administrator will review your request.
            You can try signing in again after approval.
          </Body>
        </div>
      )}

      <div className={styles.authButtons}>
        <GithubLinkButton
          fullWidth
          href={`/api/web/auth/federation/login/github${returnUrlQuery}`}
          routerOptions={{ external: true }}
        >
          Sign in with GitHub
        </GithubLinkButton>

        <LinkButton
          variant="outlined"
          color="primary"
          fullWidth
          leadingIcon={<GoogleLogomark />}
          href={`/api/web/auth/federation/login/google${returnUrlQuery}`}
          routerOptions={{ external: true }}
        >
          Sign in with Google
        </LinkButton>
      </div>

      <OrSeparator label="or use email" />

      <div className={styles.emailFields}>
        <TextInput
          type="email"
          name="email"
          placeholder="Email"
          aria-label="Email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className={styles.submitRow}>
        <div className={styles.signupLinkInline}>
          Don&apos;t have an account?{' '}
          <Link href="/signup"><b>Sign up</b></Link>
        </div>
        <Button
          variant="filled"
          color="primary"
          type="submit"
          loading={loading}
          disabled={loading}
        >
          Continue
        </Button>
      </div>

      <LegalFooter />
    </form>
  )

  const renderCheckEmailStep = () => (
    <div className={styles.mainContent}>
      <div className={styles.heading}>
        <Headline scale="large">Check your email</Headline>
      </div>

      <div className={styles.centeredContent}>
        <div className={styles.emailIconCircle}>
          <EnvelopeIcon width={32} height={32} />
        </div>

        <Body scale="large" color="on-surface-variant">
          If you have an account, we sent a login link to
        </Body>
        <Body scale="large" color="on-surface" prominent>
          {email}
        </Body>
        <Body scale="medium" color="on-surface-variant">
          Click the link in the email to sign in.<br />
          The link expires in 15 minutes.
        </Body>
      </div>

      <div className={styles.hintBox}>
        <Body scale="medium" color="on-surface-variant">
          Didn&apos;t receive an email? Check your spam folder or{' '}
          <Link onPress={handleBackToEmail}>try a different email</Link>
        </Body>
      </div>

      <Button variant="outlined" fullWidth onPress={handleBackToEmail}>
        Use a different email
      </Button>

      <footer className={styles.signupLink}>
        No account?{' '}
        <Link href="/signup"><b>Sign up here</b></Link>
      </footer>

      <LegalFooter />
    </div>
  )

  const renderCheckingAuthStep = () => (
    <div className={styles.centeredContent}>
      <Spinner />
    </div>
  )

  const renderSSORedirectStep = () => (
    <div className={styles.centeredContent}>
      <div className={styles.heading}>
        <Headline scale="large">Redirecting...</Headline>
      </div>
      <Spinner />
      <Body scale="medium" color="on-surface-variant">
        Taking you to your organization&apos;s sign-in page...
      </Body>
    </div>
  )

  const renderPasswordStep = () => (
    <form className={styles.form} onSubmit={handlePasswordSubmit}>
      <div className={styles.heading}>
        <Headline scale="large">Sign in to Oplane</Headline>
      </div>

      {error && (
        <div className={styles.errorBanner} role="alert">
          <Body scale="medium">{error}</Body>
        </div>
      )}

      <div className={styles.passwordFields}>
        <TextInput
          type="email"
          name="email"
          placeholder="Email"
          aria-label="Email"
          autoComplete="email"
          value={email}
          readOnly
        />

        <TextInput
          type="password"
          name="password"
          placeholder="Enter your password"
          aria-label="Password"
          autoComplete="current-password"
          autoFocus
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className={styles.buttonRow}>
        <Button
          variant="outlined"
          color="primary"
          onPress={handleBackToEmail}
        >
          Cancel
        </Button>
        <Button
          variant="filled"
          color="primary"
          type="submit"
          loading={loading}
          disabled={loading}
        >
          Sign in
        </Button>
      </div>

      <LegalFooter />
    </form>
  )

  return (
    <div className={styles.root}>
      <div className={styles.leftColumn}>
        <div className={styles.card}>
          {step === 'checking_auth' && renderCheckingAuthStep()}
          {step === 'email' && renderEmailStep()}
          {step === 'password' && renderPasswordStep()}
          {step === 'check_email' && renderCheckEmailStep()}
          {step === 'sso_redirect' && renderSSORedirectStep()}
        </div>
      </div>

      <div className={styles.rightColumn}>
        <div className={styles.demoContainer}>
          <WelcomeBack />
        </div>
      </div>
    </div>
  )
}

export default Login
